import type {ReactElement} from 'react';
import React from 'react';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {ContextualMenuToggleOption} from '@Components/contextual-menu/components/contextual-menu-toggle-option';
import {type ColorFillStorage, ContextualMenuColorFillOption} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {getColorObjectArrayFromFillColor} from '@Utils/color.util';
import {ColorFillPropertyType} from '@Components/color-fill';
import {ContextualMenuSliderOptions} from '@Components/contextual-menu/components/contextual-menu-slider-options';
import type {TextBackgroundObject} from '@PosterWhiteboard/items/text-item/text-background';
import {BaseVectorRoundnessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-vector-roundness-option/base-vector-roundness-option';

interface TextItemBaseBackgroundOptionProps {
  background: TextBackgroundObject;
  onClickColorFill(): void;
  onUpdateOpacity(fillAlpha: number, undoable: boolean): void;
  onUpdateRoundness(roundness: number, undoable: boolean): void;
  onChangeFillType(newType: FillTypes): void;
}

export function TextItemBaseBackgroundOption({...props}: TextItemBaseBackgroundOptionProps): ReactElement<any> | null {
  const onClickColorFill = (): void => {
    props.onClickColorFill();
  };

  const isToggled = (): boolean => {
    return props.background.fill.fillType !== FillTypes.NONE;
  };

  const onToggleHandler = (): void => {
    if (props.background.fill.fillType === FillTypes.SOLID) {
      props.onChangeFillType(FillTypes.NONE);
    } else {
      props.onChangeFillType(FillTypes.SOLID);
    }
  };

  const getSubOptions = (): ReactElement<any> | null => {
    if (props.background.fill.fillType !== FillTypes.NONE) {
      return (
        <>
          {getColorFillOption()}
          {getColorOpacityOption()}
          {getBackgroundRoundnessOption()}
        </>
      );
    }
    return null;
  };

  const getFillType = (): ColorFillPropertyType => {
    if (props.background.fill.fillType === FillTypes.LINEAR_GRADIENT) {
      return ColorFillPropertyType.LINEAR;
    }

    if (props.background.fill.fillType === FillTypes.RADIAL_GRADIENT) {
      return ColorFillPropertyType.RADIAL;
    }

    return ColorFillPropertyType.SOLID;
  };

  const getColorObject = (): ColorFillStorage[] => {
    return [
      {
        id: 'text-background-color-fill-vector',
        fillType: getFillType(),
        color: getColorObjectArrayFromFillColor(props.background.fill.fillColor),
      },
    ];
  };

  const getColorOpacityOption = (): ReactElement<any> => {
    return (
      <ContextualMenuSliderOptions
        id="text-background-color-opacity-option"
        value={props.background.fill.fillColor[0][3]}
        label={window.i18next.t('pmwjs_opacity')}
        range={{
          min: 0,
          max: 1,
        }}
        transformDisplay={{
          min: 0,
          max: 100,
        }}
        onChange={(updatedValue: number): void => {
          props.onUpdateOpacity(updatedValue, false);
        }}
        onChangeEnded={(updatedValue: number): void => {
          props.onUpdateOpacity(updatedValue, true);
        }}
      />
    );
  };

  const getBackgroundRoundnessOption = (): ReactElement<any> => {
    return (
      <BaseVectorRoundnessOption
        fillRoundness={props.background.rx}
        onChangeRoundness={(val: number, undoable = true): void => {
          props.onUpdateRoundness(val, undoable);
        }}
      />
    );
  };

  const getColorFillOption = (): ReactElement<any> => {
    return <ContextualMenuColorFillOption id="text-item-background-fill-color-option" label={window.i18next.t('pmwjs_color')} data={getColorObject()} onClick={onClickColorFill} />;
  };

  return (
    <ContextualMenuToggleOption id="text-item-background-option-toggle" label={window.i18next.t('pmwjs_background')} isChecked={isToggled()} onClick={onToggleHandler}>
      {getSubOptions()}
    </ContextualMenuToggleOption>
  );
}
